import type {Table as ReactTable} from '@tanstack/react-table';
import {X} from 'lucide-react';
import {useTranslation} from 'react-i18next';

import {Button} from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {toggleVariants} from '@/components/ui/toggle';
import {cn} from '@/utils/ui';

interface TableVisibilityProps<TData> {
  table: ReactTable<TData>;
}

function TableVisibility<TData>({table}: TableVisibilityProps<TData>) {
  const {t} = useTranslation();
  const visibilityColumns = table
    .getAllLeafColumns()
    .filter(column => column.getCanHide());

  const visibleColumns = visibilityColumns.filter(column =>
    column.getIsVisible()
  );

  return (
    <div className="flex flex-row flex-wrap items-center gap-1">
      {!!visibilityColumns.length && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="secondary" size="small">
              {t('Columns')}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            {visibilityColumns.map(column => (
              <DropdownMenuItem
                className={cn(
                  'flex items-center gap-1',
                  column.getIsVisible() && 'font-medium text-secondary-500'
                )}
                onClick={() => {
                  column.getToggleVisibilityHandler()({
                    target: {checked: !column.getIsVisible()},
                  });
                }}
                key={column.id}
              >
                {column.columnDef?.header?.toString() || column.id}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {visibleColumns.map(column => (
        <div
          key={column.id}
          className="inline-flex h-11 items-center justify-center"
        >
          <div
            className={cn(
              toggleVariants({variant: 'tag', size: 'small'}),
              'gap-2'
            )}
          >
            <span>{column.columnDef?.header?.toString() || column.id}</span>
            <Button
              type="button"
              variant="warning"
              size="text"
              className="rounded-full p-0.5"
              onClick={() => {
                column.getToggleVisibilityHandler()({target: {checked: false}});
              }}
            >
              <span className="sr-only">
                {t('Delete')}{' '}
                {column.columnDef?.header?.toString() || column.id}
              </span>
              <X size={14} />
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}

export default TableVisibility;
